// hooks/useFormValidation.js
import { formatEther } from 'viem';
import { tokenAmount } from '../../../utils/tokenAmount';

export const useFormValidation = (tokenBalance) => {
    // Helper function to validate URL format
    const isValidUrl = (string) => {
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (_) {
            return false;
        }
    };

    // Helper function to validate website URL
    const validateWebsite = (website) => {
        // console.log('Validating website:', website);

        if (!website || !website.trim()) {
            // console.log('Website is empty, skipping validation');
            return null; // Optional field
        }

        const trimmedWebsite = website.trim();
        // console.log('Trimmed website:', trimmedWebsite);

        // Add https:// if no protocol is provided
        let url = trimmedWebsite;
        if (!url.match(/^https?:\/\//)) {
            url = 'https://' + url;
            // console.log('Added https://, new URL:', url);
        }

        const isValid = isValidUrl(url);
        // console.log('URL validation result:', isValid);

        if (!isValid) {
            return "Please enter a valid website URL (e.g., https://example.com)";
        }

        // Additional checks for common issues
        try {
            const urlObj = new URL(url);
            if (!urlObj.hostname || urlObj.hostname.length < 3) {
                return "Please enter a valid website URL with a proper domain";
            }
            if (!urlObj.hostname.includes('.')) {
                return "Please enter a valid website URL with a proper domain (e.g., example.com)";
            }
        } catch (e) {
            // console.log('Additional URL validation failed:', e);
            return "Please enter a valid website URL (e.g., https://example.com)";
        }

        // console.log('Website validation passed');
        return null;
    };

    // Helper function to validate Twitter handle
    const validateTwitter = (twitter) => {
        if (!twitter.trim()) return null; // Optional field
        const handle = twitter.trim();

        if (handle.includes('twitter.com') || handle.includes('x.com')) {
            if (!isValidUrl(handle)) {
                return "Please enter a valid Twitter/X URL";
            }
            const urlPattern = /(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)/;
            const match = handle.match(urlPattern);
            if (!match) return "Invalid Twitter/X URL format";
            const username = match[1];
            if (!/^[a-zA-Z][a-zA-Z0-9_]{3,14}$/.test(username)) {
                return "Twitter/X handle must be 4–15 characters, start with a letter, and contain only letters, numbers, and underscores";
            }
        } else {
            const username = handle.startsWith('@') ? handle.slice(1) : handle;
            if (!/^[a-zA-Z][a-zA-Z0-9_]{3,14}$/.test(username)) {
                return "Twitter/X handle must be 4–15 characters, start with a letter, and contain only letters, numbers, and underscores";
            }
        }
        return null;
    };

    // Helper function to validate Instagram handle
    const validateInstagram = (instagram) => {
        if (!instagram.trim()) return null; // Optional field
        const handle = instagram.trim();

        if (handle.includes('instagram.com')) {
            if (!isValidUrl(handle)) {
                return "Please enter a valid Instagram URL";
            }
            const urlPattern = /instagram\.com\/([a-zA-Z0-9_.]+)/;
            const match = handle.match(urlPattern);
            if (!match) return "Invalid Instagram URL format";
            const username = match[1];
            if (!/^(?![_.])[a-zA-Z0-9._]{3,30}(?<!\.)$/.test(username) || username.includes("..")) {
                return "Instagram handle must be 3–30 characters, cannot start with . or _, cannot end with ., and cannot contain consecutive periods";
            }
        } else {
            const username = handle.startsWith('@') ? handle.slice(1) : handle;
            if (!/^(?![_.])[a-zA-Z0-9._]{3,30}(?<!\.)$/.test(username) || username.includes("..")) {
                return "Instagram handle must be 3–30 characters, cannot start with . or _, cannot end with ., and cannot contain consecutive periods";
            }
        }
        return null;
    };


    // Helper function to validate Discord handle
    const validateDiscord = (discord) => {
        if (!discord.trim()) return null; // Optional field

        const handle = discord.trim();

        // Check if it's a Discord invite URL
        if (handle.includes('discord.gg') || handle.includes('discord.com/invite')) {
            if (!isValidUrl(handle)) {
                return "Please enter a valid Discord invite URL";
            }
        } else {
            // Validate as username (new format without discriminator or old format with #discriminator)
            if (handle.includes('#')) {
                // Old format: username#1234
                const [username, discriminator] = handle.split('#');
                if (!/^[a-zA-Z0-9_.]{2,32}$/.test(username)) {
                    return "Discord username must be 2-32 characters long";
                }
                if (!/^\d{4}$/.test(discriminator)) {
                    return "Discord discriminator must be 4 digits";
                }
            } else {
                // New format: just username (or @username)
                const username = handle.startsWith('@') ? handle.slice(1) : handle;
                if (!/^[a-z0-9_.]{2,32}$/.test(username)) {
                    return "Discord username must be 2-32 characters long and contain only lowercase letters, numbers, dots, and underscores";
                }
            }
        }

        return null;
    };

    const validateStep1 = (formData) => {
        const newErrors = {};

        // Existing validations
        if (!formData.logo) {
            newErrors.logo = "Project logo is required";
        }

        if (!formData.rewardToken.trim()) {
            newErrors.rewardToken = "Reward token address is required";
        } else if (!/^0x[a-fA-F0-9]{40}$/.test(formData.rewardToken)) {
            newErrors.rewardToken = "Invalid EVM address format";
        } else if ((tokenBalance !== null && tokenBalance === 0n) || !tokenBalance) {
            newErrors.rewardToken = "You don't have any balance of this token. Cannot create pool.";
        }

        if (!formData.projectName.trim()) {
            newErrors.projectName = "Project name is required";
        }

        if (!formData.tagline.trim()) {
            newErrors.tagline = "Tagline is required";
        }

        // Social media validations (optional fields)
        const websiteError = validateWebsite(formData.website || '');
        if (websiteError) {
            newErrors.website = websiteError;
        }

        const twitterError = validateTwitter(formData.twitter || '');
        if (twitterError) {
            newErrors.twitter = twitterError;
        }

        const instagramError = validateInstagram(formData.instagram || '');
        if (instagramError) {
            newErrors.instagram = instagramError;
        }

        const discordError = validateDiscord(formData.discord || '');
        if (discordError) {
            newErrors.discord = discordError;
        }

        // Debug logging to help identify issues
        // console.log('Form validation results:', {
        //     formData: {
        //         website: formData.website,
        //         twitter: formData.twitter,
        //         instagram: formData.instagram,
        //         discord: formData.discord
        //     },
        //     errors: {
        //         website: websiteError,
        //         twitter: twitterError,
        //         instagram: instagramError,
        //         discord: discordError
        //     },
        //     totalErrors: Object.keys(newErrors).length
        // });

        return newErrors;
    };

    const validateStep2 = (formData) => {
        const newErrors = {};
        // console.log(formData, 'formDataformDataformDataformData');

        if (!formData.totalRewards.trim()) {
            newErrors.totalRewards = "Total rewards amount is required";
        } else {
            const rewardAmount = parseFloat(formData.totalRewards);
            if (isNaN(rewardAmount) || rewardAmount <= 0) {
                newErrors.totalRewards = "Please enter a valid reward amount";
            } else if (tokenBalance !== null) {
                const balanceInEther = parseFloat(tokenAmount(tokenBalance, formData?.tokenDecimals)?.formatted || '0');
                if (rewardAmount > balanceInEther) {
                    newErrors.totalRewards = `Insufficient balance. Available: ${balanceInEther.toFixed(6)}`;
                }
            }
        }

        if (!formData.poolEndDate) {
            newErrors.poolEndDate = "Pool end date is required";
        } else {
            const now = new Date();
            if (formData.poolEndDate <= now) {
                newErrors.poolEndDate = "Pool end date must be in the future";
            }
        }

        return newErrors;
    };

    return { validateStep1, validateStep2 };
};